import { useCallback } from "react"
import { GetApp } from "../../App"
import { useEditState } from "../../App/useEditState"
import { logger } from "../../App/Logger"
import { Localization } from "../../App/Localization"
import { BlockField, BlockRow } from "../../Components/Block"
import { EditGroup } from "../../Components/EditGroup"
import { Overlay, OverlayTitle } from "../../Components/Overlay"
import { ShipDataViewData } from "../ShipData/Context"
import { useShipUserWriters } from "../ShipData/UsersDataContext"
import { userEditableFields } from "./Data"
import { InviteUserOverlayProps } from "./Params"

// ---------------------------------------------------------------------------
// Invite User Overlay Component
// ---------------------------------------------------------------------------

export function InviteUserOverlay({ ship, user, editable, shipUserCrewRoleOptions, shipUserLevelOptions, onClose, t }: InviteUserOverlayProps) {
  const app = GetApp();
  // Route the re-invite (PUT) through the flag-selected writer boundary (falls back to
  // typed-REST when rendered standalone, outside a UsersView provider). (issue #1186)
  const writers = useShipUserWriters(ship.id);
  const [edited, onEdited, resetEdited] = useEditState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const inviteShipUser = useCallback(async (_data: FormData, values: any) => {
    if (!user) {
      return;
    }
    // The server auto-dispatches PUT to a re-invite when userId is null and an email
    // is supplied (see reinviteShipUser) -- this row's own userId was cleared by a
    // prior deactivate/cascade, so this is always the re-invite path.
    try {
      const result = await writers.updateShipUser(user.id, values);
      logger.debug("inviteShipUser result", result.user?.id);
      if (result.user) {
        resetEdited();
        onClose();
      } else {
        logger.error("No user returned from API");
      }
    } catch (err) {
      logger.error("inviteShipUser failed", err);
    }
  }, [writers, user, onClose, resetEdited]);

  if (!user) {
    return null;
  }
  return (
    <Overlay closeCallback={onClose} closeBackground={true}>
      <OverlayTitle title={t("user_invite_title")} closeCallback={onClose} />

      <EditGroup key={user.id} changed={edited} editableFields={userEditableFields} manualSave={true} hideButton={true} onSave={inviteShipUser} className="Overlay-body">
        <BlockRow>
          <BlockField label={t("user_name")} className="block_field_small_22_fix">
            <input value={user.name} readOnly />
          </BlockField>
          <BlockField label={t("user_shortname")} className="block_field_small_12">
            <input value={user.shortname} readOnly />
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={t("user_crew_role")} className="block_field_small_10">
            <select className="" defaultValue={user.shipUserCrewRole} name="shipUserCrewRole" onChange={onEdited} disabled={!editable}>
              {shipUserCrewRoleOptions}
              {ShipDataViewData.legacyCrewRoleOption(t, user.shipUserCrewRole)}
            </select>
          </BlockField>
          <BlockField label={t("ship_user_level")} className="block_field_small_22_fix">
            <select className="" defaultValue={user.shipUserLevel} name="shipUserLevel" onChange={onEdited} disabled={!editable}>
              {shipUserLevelOptions}
            </select>
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={t("user_email")} className="block_field_small_32">
            <input name="email" onChange={onEdited} required />
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={t("language")} className="block_field_small_32">
            <select name="language" defaultValue={app.language} onChange={onEdited} disabled={!editable}>
              {Object.keys(Localization.Languages).map((lang) => (
                <option value={lang} key={lang}>{t(`lang_${lang}`)}</option>
              ))}
            </select>
          </BlockField>
        </BlockRow>

        <BlockRow actions={true}>
          <BlockField></BlockField>
          <BlockField>
            <button type="button" className="button" onClick={onClose}>{t("cancel")}</button>
            <button className="button default" type="submit">{t("user_invite")}</button>
          </BlockField>
        </BlockRow>
      </EditGroup>
    </Overlay>
  );
}
